import React, { useState, useRef, useEffect } from 'react';

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options...",
  label = "",
  disabled = false,
  className = "",
  displayKey = "name", // Key to display in the dropdown
  valueKey = "id", // Key to use as value
  emptyMessage = "No options available"
}) => {
  // Ensure options is always an array and filter out null/undefined entries
  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];
  const safeSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = safeOptions.filter(option => {
    const displayText = typeof option === 'string' ? option : option[displayKey];
    if (!displayText) return false; // Skip options without display text
    return displayText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handle option selection/deselection
  const handleOptionClick = (option) => {
    if (!option) return; // Skip null/undefined options
    const value = typeof option === 'string' ? option : option[valueKey];
    if (value === undefined || value === null) return; // Skip options without valid values
    const isSelected = safeSelectedValues.includes(value);
    
    let newSelectedValues;
    if (isSelected) {
      // Remove from selection
      newSelectedValues = selectedValues.filter(v => v !== value);
    } else {
      // Add to selection
      newSelectedValues = [...selectedValues, value];
    }
    
    onChange(newSelectedValues);
  };

  // Remove selected item
  const removeSelected = (value) => {
    const newSelectedValues = safeSelectedValues.filter(v => v !== value);
    onChange(newSelectedValues);
  };

  // Get display text for selected values
  const getDisplayText = (value) => {
    if (typeof safeOptions[0] === 'string') {
      return value;
    }
    const option = safeOptions.find(opt => opt && opt[valueKey] === value);
    return option ? option[displayKey] : value;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      {/* Selected items display */}
      <div className="min-h-[40px] border border-gray-300 rounded-lg p-2 bg-white cursor-pointer"
           onClick={() => !disabled && setIsOpen(!isOpen)}>
        {safeSelectedValues.length === 0 ? (
          <span className="text-gray-500">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {safeSelectedValues.map(value => (
              <span
                key={value}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
              >
                {getDisplayText(value)}
                {!disabled && (
                  <button
                    type="button"
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelected(value);
                    }}
                  >
                    <span className="sr-only">Remove</span>
                    <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        
        {/* Dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          {safeOptions.length > 5 && (
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? 'No matching options' : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                if (!option) return null; // Skip null/undefined options
                const value = typeof option === 'string' ? option : option[valueKey];
                const displayText = typeof option === 'string' ? option : option[displayKey];
                
                if (value === undefined || value === null || !displayText) return null; // Skip invalid options
                
                const isSelected = safeSelectedValues.includes(value);

                return (
                  <div
                    key={value}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                      isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(option);
                    }}
                  >
                    <span>{displayText}</span>
                    {isSelected && (
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                );
              }).filter(Boolean) // Remove null entries
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
